import { useCallback, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../globals";

/**
 * Borradores como WhatsApp (Esteban, 2026-10-14): lo que se deja escrito en un chat de WappChat o WappTeam sigue ahí
 * al volver, aunque se haya pasado a la otra sección, a otro chat o se recargue la página.
 *
 * Todo vive en el navegador (memoria + una sola llave de localStorage); el servidor no se entera. Se guarda por
 * persona (dos usuarias en la misma compu no ven lo de la otra), caducan a los 7 días y hay tope de cuántos se
 * guardan. localStorage se escribe agrupado (una vez cada medio segundo como mucho, y al cerrar la pestaña).
 *
 * Llaves: "chat:<prospectId>" mensaje de WappChat, "nota:<prospectId>" nota de WappChat, "team:<canalId>" WappTeam
 * (la misma para la ventanita y la pantalla completa), "eqm:<canalId>" las @menciones elegidas de ese borrador.
 */

const LLAVE = "wapp.borradores";
const CADUCA_MS = 7 * 24 * 3600e3;
const MAX_BORRADORES = 100;
const MAX_LARGO = 20000;

type Guardado = { t: string; at: number };
let memoria: Record<string, Guardado> | null = null;
let pendiente: ReturnType<typeof setTimeout> | null = null;

const cargar = (): Record<string, Guardado> => {
	if (memoria) return memoria;
	memoria = {};
	try {
		const crudo = JSON.parse(localStorage.getItem(LLAVE) || "{}");
		const limite = Date.now() - CADUCA_MS;
		Object.keys(crudo || {}).forEach((k) => {
			const g = crudo[k];
			if (g && typeof g.t === "string" && g.t && g.at > limite) memoria![k] = g;
		});
	} catch { /* sin almacenamiento o dañado: se empieza vacío */ }
	return memoria;
};

const escribir = () => {
	pendiente = null;
	if (!memoria) return;
	const ks = Object.keys(memoria);
	if (ks.length > MAX_BORRADORES) {
		ks.sort((a, b) => memoria![b].at - memoria![a].at).slice(MAX_BORRADORES).forEach((k) => delete memoria![k]);
	}
	try {
		if (Object.keys(memoria).length) localStorage.setItem(LLAVE, JSON.stringify(memoria));
		else localStorage.removeItem(LLAVE);
	} catch { /* lleno o bloqueado: queda en memoria mientras la pestaña siga abierta */ }
};

if (typeof window !== "undefined") window.addEventListener("pagehide", () => { if (pendiente) { clearTimeout(pendiente); escribir(); } });

const llaveDe = (yo: number | null, k: string | null): string | null => (yo && k ? yo + "|" + k : null);

export const leerBorrador = (yo: number | null, k: string | null): string => {
	const ll = llaveDe(yo, k);
	return ll ? cargar()[ll]?.t || "" : "";
};

export const guardarBorrador = (yo: number | null, k: string | null, t: string): void => {
	const ll = llaveDe(yo, k);
	if (!ll) return;
	const m = cargar();
	if ((m[ll]?.t || "") === t) return;
	if (t.trim()) m[ll] = { t: t.slice(0, MAX_LARGO), at: Date.now() };
	else delete m[ll];
	if (!pendiente) pendiente = setTimeout(escribir, 500);
};

/** Deja un texto sugerido como borrador de ese chat y lo escribe ya (otra pestaña que se abre enseguida lo lee de localStorage). */
export const dejarBorrador = (yo: number | null, k: string | null, t: string): void => {
	guardarBorrador(yo, k, t);
	if (pendiente) clearTimeout(pendiente);
	escribir();
};

export const useYo = (): number | null => useSelector((state: RootState) => (state.rootReducer.sessionState as any).user?.id ?? null);

type Poner = (t: string | ((antes: string) => string)) => void;

/**
 * Como useState<string>, pero el texto se queda guardado por llave (k = null: no se guarda). Al cambiar la llave
 * aparece el borrador de esa conversación. El que escribe con un setter de antes (un envío que terminó después de
 * cambiar de chat) solo toca el borrador de su conversación, nunca el de la que está abierta.
 * soloPantalla: cambia el cuadro sin tocar el borrador (WappTeam al editar un mensaje ya enviado); restaurar: vuelve a él.
 */
export const useBorrador = (k: string | null): [string, Poner, { soloPantalla: (t: string) => void; restaurar: () => void }] => {
	const yo = useYo();
	const llave = llaveDe(yo, k);
	const [s, setS] = useState<{ ll: string | null; t: string }>(() => ({ ll: llave, t: leerBorrador(yo, k) }));
	let actual = s;
	if (s.ll !== llave) { actual = { ll: llave, t: leerBorrador(yo, k) }; setS(actual); }

	const poner: Poner = useCallback((t) => {
		const nuevo = typeof t === "function" ? t(leerBorrador(yo, k)) : t;
		guardarBorrador(yo, k, nuevo);
		setS((prev) => (prev.ll === llave ? { ll: llave, t: nuevo } : prev));
	}, [yo, k, llave]);
	const soloPantalla = useCallback((t: string) => setS((prev) => (prev.ll === llave ? { ll: llave, t } : prev)), [llave]);
	const restaurar = useCallback(() => setS({ ll: llave, t: leerBorrador(yo, k) }), [yo, k, llave]);
	return [actual.t, poner, { soloPantalla, restaurar }];
};
